import React, { useState, useEffect } from 'react';
import { Step } from 'components/RequestAQuote/Step/Step';
import { InformationForm } from 'components/RequestAQuote/InformationForm/InformationForm';
import { VehicleSelector } from 'components/RequestAQuote/VehicleSelector/VehicleSelector';
import { DealerLocator } from 'components/DealerLocator/DealerLocator';
import {
  IBAPAccessory,
  IFormData,
  IQuoteRequest,
  IRAQConfigurationOffers,
  IRequestAQuoteData,
} from 'interfaces/requestAQuote';
import {
  removeSelectedDealer,
  selectedDealer,
  setSelectedDealer,
} from 'redux/slices/dealerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from './RequestAQuoteForm.styles';
import TextRender from 'components/Global/TextRender/TextRender';
import { getRAQData } from 'redux/slices/vehicleSlice';
import { usePostQuoteRequest } from 'hooks/usePostQuoteRequest';
import { removeMinHeight } from 'helpers/Layout';
import { cleanHTMLFromString } from 'helpers/FormatText';
import Cookies from 'universal-cookie';
import { IReduxAccessory } from 'interfaces/vehicle';
import {
  FormStatusOptions,
  IRequestAQuote,
} from '../../Modal/RequestAQuoteModal/RequestAQuoteModal';

const raqScrollTop = (
  scrollContainerId: string | undefined,
  isMinHeightRemoval: boolean
) => {
  const scrollElement = scrollContainerId
    ? document.getElementById(scrollContainerId)
    : window;
  scrollElement?.scrollTo({ top: 0 });
  isMinHeightRemoval && removeMinHeight();
};

const getRequestVerificationToken = () => {
  const requestVerificationTokenSelector =
    'input[name="__RequestVerificationToken"]';
  return (
    (
      document.querySelector(
        requestVerificationTokenSelector
      ) as HTMLInputElement
    )?.value || ''
  );
};

const RequestAQuoteForm = ({
  configuration,
  data,
  isInModal,
  title,
  description,
  scrollContainerId,
  shoppingTool,
  setFormData,
  hideChangeDealer,
  setFormStatus,
}: IRequestAQuote) => {
  // Hooks
  const classes = useStyles();
  const dispatch = useDispatch();
  // Data
  const { pageContent, vehicleData } = data || {};
  const { labels, informationForm } = pageContent || {};
  const { exteriorColor, interiorColor } = vehicleData || {};
  const customDealer = vehicleData?.inventory?.dealer;
  // States
  const [stepThreeEnabled, setStepThreeEnabled] = useState(false);
  const [showGlobalError, setShowGlobalError] = useState(false);
  const [showModelError, setShowModelError] = useState(false);
  const [showPackageError, setShowPackageError] = useState(false);
  // Selectors
  const currentSelectedDealer = useSelector(selectedDealer);
  const raqVehicleData = useSelector(getRAQData);

  const { isLoading, mutate } = usePostQuoteRequest(
    () => {
      raqScrollTop(scrollContainerId, !isInModal);
      if (setFormStatus) setFormStatus(FormStatusOptions.success);
    },
    () => {
      raqScrollTop(scrollContainerId, !isInModal);
      if (setFormStatus) setFormStatus(FormStatusOptions.error);
    }
  );

  const requestVerificationToken = getRequestVerificationToken();

  const getAccessories = () => {
    return raqVehicleData.accessories
      ? (raqVehicleData.accessories.reduce(function (
          filtered: IBAPAccessory[],
          accessory: IReduxAccessory
        ) {
          if (
            accessory.categoryName !== 'Packages' &&
            accessory.name !== 'empty'
          ) {
            const mappedAccessory: IBAPAccessory = {
              Name: cleanHTMLFromString(accessory.enName),
              Price: accessory.price,
              Code: accessory.opCode,
              Type: accessory.categoryName === 'Exterior' ? 'E' : 'I',
            };
            filtered.push(mappedAccessory);
          }
          return filtered;
        },
        []) as IBAPAccessory[])
      : null;
  };

  const getModelId = () => {
    return raqVehicleData.modelId.toLowerCase() === 'undecided'
      ? null
      : raqVehicleData.modelId;
  };

  const getExteriorColorProp = (prop: string): string => {
    let exteriorData = '';
    switch (prop) {
      case 'exteriorColorCode':
        exteriorData =
          raqVehicleData.exteriorColor.internalCode !== 'empty'
            ? raqVehicleData.exteriorColor.internalCode
            : exteriorColor?.manufacturingCode || '';
        break;
      case 'exteriorColorIdent':
        exteriorData =
          raqVehicleData.exteriorColor.colorIdentifier !== 'empty'
            ? raqVehicleData.exteriorColor.colorIdentifier
            : exteriorColor?.id || '';
        break;
      case 'exteriorColorName':
        exteriorData =
          raqVehicleData.exteriorColor.name || exteriorColor?.name || '';
        break;
      case 'exteriorColorPriceDelta':
        exteriorData =
          raqVehicleData.exteriorColor.price !== 0
            ? raqVehicleData.exteriorColor.price.toString()
            : '';
        break;
    }
    return exteriorData;
  };

  const getInteriorColorProp = (prop: string): string => {
    let interiorData = '';
    switch (prop) {
      case 'interiorColorCode':
        interiorData =
          raqVehicleData.interiorColor.internalCode !== 'empty'
            ? raqVehicleData.interiorColor.internalCode
            : interiorColor?.code || '';
        break;
      case 'interiorColorIdent':
        interiorData =
          raqVehicleData.interiorColor.colorIdentifier !== 'empty'
            ? raqVehicleData.interiorColor.colorIdentifier
            : interiorColor?.id || '';
        break;
      case 'interiorColorName':
        interiorData =
          raqVehicleData.interiorColor.name || interiorColor?.name || '';
        break;
    }
    return interiorData;
  };

  const sendQuoteRequest = (formData: IFormData) => {
    const cookies = new Cookies();
    const shoppingToolSource =
      shoppingTool || labels?.defaultShoppingToolSource || '';
    const cookie = cookies.get('_td_global');
    const quoteRequestObject: IQuoteRequest = {
      accessories: getAccessories(),
      adobeTransactionId: cookie,
      vehicleModelSeries: raqVehicleData.modelSeries,
      modelMarketingName: `${raqVehicleData.modelYear} ${raqVehicleData.modelSeries}`,
      modelYear: raqVehicleData.modelYear,
      modelId: getModelId(),
      exteriorColorCode: getExteriorColorProp('exteriorColorCode'),
      exteriorColorIdent: getExteriorColorProp('exteriorColorIdent'),
      exteriorColorName: getExteriorColorProp('exteriorColorName'),
      exteriorColorPriceDelta: getExteriorColorProp('exteriorColorPriceDelta'),
      interiorColorCode: getInteriorColorProp('interiorColorCode'),
      interiorColorIdent: getInteriorColorProp('interiorColorIdent'),
      interiorColorName: getInteriorColorProp('interiorColorName'),
      totalAsConfigured:
        raqVehicleData.totalAsConfigured !== 0
          ? raqVehicleData.totalAsConfigured.toString()
          : '',
      dealerId: currentSelectedDealer?.dealerNumber || '',
      localDealerName: currentSelectedDealer?.name || '',
      vinNumber: configuration?.vinNumber || '',
      offerId: (configuration as IRAQConfigurationOffers).offers || null,
      source: shoppingToolSource,
      zipCode: formData['raq-zipcode'],
      shoppingTool: shoppingToolSource,
      provider: 'Acura Website',
      tracking: {
        campaingName: '',
        shoppingTool: shoppingToolSource,
      },
      addressScrubbing: false,
      userInfo: {
        __RequestVerificationToken: requestVerificationToken,
        firstName: formData['first-name'],
        lastName: formData['last-name'],
        email: formData.email,
        zipcode: formData['raq-zipcode'],
        phone: formData.phone,
        comments: formData.comments,
        terms: 'terms',
      },
    };
    mutate(quoteRequestObject);
  };

  const onSubmit = (formData: IFormData) => {
    if (
      !currentSelectedDealer ||
      !raqVehicleData.modelSeries ||
      !raqVehicleData.modelId
    ) {
      setShowGlobalError(true);
      raqScrollTop(scrollContainerId, !isInModal);
    } else {
      if (setFormData) {
        setFormData(formData);
      }
      setShowGlobalError(false);
      sendQuoteRequest(formData);
    }
    if (!raqVehicleData.modelId) {
      setShowModelError(true);
      setShowPackageError(true);
    } else {
      setShowModelError(false);
      setShowPackageError(false);
    }
  };

  useEffect(() => {
    if (
      currentSelectedDealer &&
      configuration?.modelYear &&
      configuration.modelSeries &&
      configuration.modelId
    ) {
      setStepThreeEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedDealer]);

  useEffect(() => {
    configuration?.dealerId &&
      currentSelectedDealer?.dealerNumber !== configuration?.dealerId &&
      dispatch(removeSelectedDealer());

    if (
      customDealer &&
      customDealer.dealerNumber !== currentSelectedDealer?.dealerNumber
    ) {
      dispatch(setSelectedDealer(customDealer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${classes.root} page-container`}>
      {(title || description) && (
        <div className={classes.header}>
          {title && (
            <TextRender
              tagName="h1"
              className="acr-title-3 test-title"
              text={labels ? labels.title : ''}
            />
          )}
          {description && (
            <TextRender
              tagName="p"
              className="acr-paragraph-4 test-description"
              text={labels ? labels.description : ''}
            />
          )}
          {showGlobalError && (
            <TextRender
              tagName="span"
              className={`acr-paragraph-4 ${classes.errorTag} test-error`}
              text={labels?.globalError || ''}
            />
          )}
        </div>
      )}
      <Step
        stepNumber={1}
        isPartial={true}
        isEnabled={true}
        sectionName={labels?.selectADealer || ''}
      >
        <div
          className={`${currentSelectedDealer ? '' : classes.dealerContainer} ${
            classes.wrapper
          }`}
        >
          <DealerLocator
            selectedDealer={
              configuration?.dealerId ? configuration?.dealerId : undefined
            }
            hideChangeDealer={hideChangeDealer}
            isInModal={isInModal}
            scrollContainerId={scrollContainerId}
            customDealerShowMoreDelta={3}
            showLocationBar={false}
            isEmbedded={true}
          />
        </div>
      </Step>
      <Step
        stepNumber={2}
        isPartial={true}
        isEnabled={!!currentSelectedDealer}
        sectionName={labels?.selectAVehicle || ''}
      >
        <VehicleSelector
          showModelError={showModelError}
          showPackageError={showPackageError}
          onEnabled={() => {
            setStepThreeEnabled(true);
          }}
          configuration={configuration}
          data={data as IRequestAQuoteData}
        />
      </Step>
      <Step
        stepNumber={3}
        isPartial={false}
        isEnabled={stepThreeEnabled}
        sectionName={labels?.enterYourInformation || ''}
        caption={labels?.required || ''}
      >
        {informationForm && (
          <InformationForm
            isLoading={isLoading}
            onSubmit={onSubmit}
            stepData={informationForm}
            isAdxEnhanced={false}
          />
        )}
      </Step>
    </div>
  );
};

export default RequestAQuoteForm;
